import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { getDevice } from "@/features/terminal/device";
import { LAUNCH, NAV, type NavItem, type NavSection } from "@/app/nav";
import { useCan, useFeature, usePermissions } from "@/shared/hooks/session";

export type VisibleItem = NavItem & { locked: boolean };
export type VisibleSection = Omit<NavSection, "items"> & { items: VisibleItem[] };

/** Разделы и пункты, доступные пользователю в текущем филиале. */
export function useVisibleNav(): VisibleSection[] {
  const can = useCan();
  const hasFeature = useFeature();
  const { data } = usePermissions();
  return useMemo(
    () =>
      NAV.map((s) => ({
        ...s,
        items: s.items.filter((i) => (!i.permission || can(i.permission)) && (!i.anyPermission || i.anyPermission.some(can))).map((i) => ({ ...i, locked: !hasFeature(i.feature) })),
      })).filter((s) => s.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, can, hasFeature],
  );
}

/** Плитки рабочих экранов (касса, кухня), доступные по правам; без функции тарифа — с замком. */
export function useVisibleLaunch(): VisibleItem[] {
  const can = useCan();
  const hasFeature = useFeature();
  const { data } = usePermissions();
  return useMemo(
    () => LAUNCH.filter((i) => !i.permission || can(i.permission)).map((i) => ({ ...i, locked: !hasFeature(i.feature) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, can, hasFeature],
  );
}

/** Куда вести «Назад» с кассы/кухни: первый доступный раздел оболочки ERP; на устройстве (касса, KDS) — никуда. */
export function useShellHome(): string | null {
  const sections = useVisibleNav();
  if (getDevice()) return null;
  const items = sections.flatMap((s) => s.items).filter((i) => !i.fullscreen);
  return (items.find((i) => !i.locked) ?? items[0])?.path ?? null;
}

/**
 * Касса и кухня часто открываются первой страницей вкладки (после входа «/» заменяется на них) —
 * тогда «Назад» браузера уводит с сайта. Подкладываем под страницу раздел ERP, а если его нет (устройство) —
 * копию самой страницы: «Назад» остаётся на ней.
 */
export function useBackGuard() {
  const location = useLocation();
  const perms = usePermissions();
  const home = useShellHome();
  useEffect(() => {
    if (perms.isPending || (window.history.state?.idx ?? 0) !== 0) return;
    // Напрямую через History API в формате react-router ({usr, key, idx}): два navigate подряд прервали бы друг друга
    const here = location.pathname + location.search + location.hash;
    window.history.replaceState({ usr: null, key: Math.random().toString(36).slice(2, 10), idx: 0 }, "", home ?? here);
    window.history.pushState({ usr: location.state, key: location.key, idx: 1 }, "", here);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, perms.isPending]);
}
